import {
  RecentSearchItem,
  recentSearchItemKey,
} from "@/hooks/use-recent-typeahead-items"
import { cn } from "cn"

import IconClock from "@/components/icons/clock"
import { EntityIcon } from "./entity-icon"
import { Photo } from "./ui/photo"

export function RecentSearchList({
  recentItems,
  highlightedIndex,
  onHighlightChange,
  onItemClick,
  className,
  headingClassName,
}: {
  recentItems: RecentSearchItem[]
  highlightedIndex: number
  onHighlightChange: (index: number) => void
  onItemClick: (item: RecentSearchItem) => void
  className?: string
  headingClassName?: string
}) {
  if (recentItems.length === 0) return null

  return (
    <>
      <div className={cn("mt-3 mb-2.5 text-2xs text-muted", headingClassName)}>
        Recent
      </div>
      <ul
        role="listbox"
        aria-label="Recent searches"
        className={cn("-mx-1.5", className)}
        onMouseLeave={() => onHighlightChange(-1)}
      >
        {recentItems.map((item, index) => {
          const isActive = index === highlightedIndex
          return (
            <li
              key={recentSearchItemKey(item)}
              role="option"
              aria-selected={isActive}
              onMouseEnter={() => onHighlightChange(index)}
              onMouseDown={() => onItemClick(item)}
              className={cn(
                "no-translate flex cursor-pointer items-center gap-2.5 rounded-xl p-1.5",
                isActive && "bg-foreground/5"
              )}
            >
              <RecentSearchRow item={item} />
            </li>
          )
        })}
      </ul>
    </>
  )
}

function RecentSearchRow({ item }: { item: RecentSearchItem }) {
  if (item.kind === "query") {
    return (
      <>
        <IconClock size={24} strokeWidth={1.25} />
        {item.term}
      </>
    )
  }

  return (
    <>
      <div className="relative flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-foreground/5">
        {item.photo ? (
          <Photo
            src={item.photo}
            className="absolute inset-0 size-full"
            width="sm"
            alt=""
          />
        ) : (
          <EntityIcon type={item.type} size={18} />
        )}
      </div>
      <div className="min-w-0">
        <div className="truncate">{item.name}</div>
        {item.subtitle && (
          <div className="truncate text-2xs text-muted">{item.subtitle}</div>
        )}
      </div>
    </>
  )
}
